import { useEffect, useRef, useState } from "react";
import { trackSectionView, trackSectionScroll } from "../utils/analytics";

/**
 * Hook for automatic section visibility and scroll tracking
 * Uses Intersection Observer to track when sections come into view
 *
 * @param {string} sectionName - Name/identifier of the section
 * @param {string} sectionId - Optional section ID
 * @param {Object} options - Configuration options
 * @returns {Object} - Section tracking ref and state
 *
 * @example
 * const { sectionRef, isVisible } = useSectionTracking('hero_section', 'hero');
 * <div ref={sectionRef}>Hero Content</div>
 */
export const useSectionTracking = (
  sectionName,
  sectionId = null,
  options = {},
) => {
  const {
    threshold = 0.5, // 50% of section must be visible
    trackScroll = true,
    trackTimeOnSection = false,
  } = options;

  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasTracked, setHasTracked] = useState(false);
  const viewStartTimeRef = useRef(null);
  const scrollPercentageRef = useRef(0);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    // Intersection Observer for visibility tracking
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const isIntersecting = entry.isIntersecting;
          setIsVisible(isIntersecting);

          // Track when section first comes into view
          if (isIntersecting && !hasTracked) {
            const pageLoadTime =
              window.pageLoadTime ||
              (window.performance?.timing?.navigationStart
                ? Date.now() - window.performance.timing.navigationStart
                : 0);
            trackSectionView(
              sectionName,
              sectionId,
              Math.round(pageLoadTime / 1000),
            );
            setHasTracked(true);
            viewStartTimeRef.current = Date.now();
          }

          // Track time on section when it goes out of view
          if (!isIntersecting && viewStartTimeRef.current && trackTimeOnSection) {
            const timeOnSection = Math.round(
              (Date.now() - viewStartTimeRef.current) / 1000,
            );
            if (timeOnSection > 0) {
              // Track time on section (you may want to add this to analytics.js)
              // trackTimeOnSection(sectionName, timeOnSection);
            }
          }
        });
      },
      {
        threshold: threshold,
        rootMargin: "0px",
      },
    );

    observer.observe(element);

    // Scroll tracking
    if (trackScroll) {
      const handleScroll = () => {
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const windowHeight = window.innerHeight;
        const elementTop = rect.top;
        const elementHeight = rect.height;

        // Calculate scroll percentage
        let scrollPercentage = 0;
        if (elementTop < windowHeight && elementTop + elementHeight > 0) {
          const visibleHeight =
            Math.min(windowHeight, elementTop + elementHeight) -
            Math.max(0, elementTop);
          scrollPercentage = Math.round((visibleHeight / elementHeight) * 100);
        }

        // Track at 25%, 50%, 75%, 100% milestones
        const milestones = [25, 50, 75, 100];
        const previousPercentage = scrollPercentageRef.current;

        milestones.forEach((milestone) => {
          if (
            scrollPercentage >= milestone &&
            previousPercentage < milestone
          ) {
            trackSectionScroll(sectionName, milestone);
          }
        });

        scrollPercentageRef.current = scrollPercentage;
      };

      window.addEventListener("scroll", handleScroll, { passive: true });
      handleScroll(); // Initial check

      return () => {
        observer.disconnect();
        window.removeEventListener("scroll", handleScroll);
      };
    }

    return () => {
      observer.disconnect();
    };
  }, [
    sectionName,
    sectionId,
    threshold,
    trackScroll,
    trackTimeOnSection,
    hasTracked,
  ]);

  return {
    sectionRef,
    isVisible,
    hasTracked,
  };
};

